import { Button, Card, Center, Divider, Group, Image, rem, Stack, Text, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconExclamationCircle, IconFileTypePdf } from "@tabler/icons-react";
import { queryOptions, useSuspenseQueries } from "@tanstack/react-query";
import { ReactNode } from "react";
import { fileApi } from "shared/api/fileApi";
import { qk } from "shared/utils/qk";
import { createMarkerComponent, createSlots } from "shared/utils/slots";
import { match, P } from "ts-pattern";

type FileItemProps = { fileId: string; children?: ReactNode };

/** @private */
const Action = createMarkerComponent();

export const FileItem = Object.assign(
    ({ fileId, children = <></> }: FileItemProps) => {
        const [
            {
                data: { fileName, fileType },
            },
            {
                data: { presignedUrl, ok },
            },
        ] = useSuspenseQueries({ queries: [queries.about(fileId), queries.presignedUrl(fileId)] });

        const expandable = !fileType.startsWith("audio/");

        const slots = createSlots(children, {
            Action,
        });

        function handleExpand() {
            if (!ok) return;

            match(fileType)
                .with("application/pdf", () => window.open(presignedUrl, "_blank")?.focus())
                .when(
                    (it) => it.startsWith("audio/"),
                    () => window.open(presignedUrl, "_blank")?.focus(),
                )
                .otherwise(() =>
                    modals.open({
                        title: (
                            <Title order={3} size="h5">
                                {fileName}
                            </Title>
                        ),
                        children: (
                            <Stack>
                                <Image src={presignedUrl} alt={`Expanded preview of ${fileName}`} />
                                <Divider />
                                <Group justify="flex-end">
                                    <Button variant="default" onClick={() => modals.closeAll()}>
                                        Close
                                    </Button>
                                </Group>
                            </Stack>
                        ),
                    }),
                );
        }

        return (
            <Card shadow="sm" p="sm" pb="xs">
                <Card.Section
                    component={ok && expandable ? "button" : "div"}
                    onClick={() => expandable && handleExpand()}
                    style={{ border: "unset", cursor: ok ? "pointer" : "unset" }}
                    aria-label={ok && expandable ? `Open preview for ${fileName}` : undefined}
                >
                    <Center>
                        <Preview fileType={fileType} presignedUrl={presignedUrl} fileName={fileName} isAvailable={ok} />
                    </Center>
                </Card.Section>
                <Divider mx="-md" />
                <Group mt="xs" gap="xs">
                    <Text size="xs">
                        {ok ? (
                            fileName
                        ) : (
                            <>
                                File Not Found &nbsp;
                                <IconExclamationCircle style={{ verticalAlign: "bottom" }} size={16} />
                            </>
                        )}
                    </Text>
                    {ok && slots.Action}
                </Group>
                {slots.rest}
            </Card>
        );
    },
    { Action },
);

/** @private */
const Preview = (props: { fileType: string; presignedUrl: string; fileName: string; isAvailable: boolean }) =>
    match(props)
        .with({ fileType: P.union("application/pdf") }, ({ fileName, isAvailable }) => (
            <Center w={rem(100)} h={rem(100)}>
                <IconFileTypePdf size={42} aria-label={isAvailable ? `Preview of ${fileName} (PDF)` : "File preview unavailable (PDF)"} />
            </Center>
        ))
        .when(
            (it) => it.fileType.startsWith("audio/"),
            ({ presignedUrl }) => (
                <Center h={rem(100)} mx="md" my="-xs">
                    <audio controls controlsList="nodownload" src={presignedUrl} />
                </Center>
            ),
        )
        .otherwise(({ presignedUrl, fileName, isAvailable }) => (
            <Image
                h={100}
                w="auto"
                maw={300}
                src={presignedUrl}
                alt={isAvailable ? `Preview of ${fileName}` : "File preview unavailable"}
            />
        ));

const queries = {
    about: (id: string) =>
        queryOptions({
            queryKey: qk("document", id),
            queryFn: () => fileApi.get(id),
        }),
    presignedUrl: (id: string) =>
        queryOptions({
            queryKey: qk("document", "presignedUrl", id),
            queryFn: () => fileApi.sign(id),
        }),
};
